import { useEffect, useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Scrolls window to top whenever the location changes (including same-route clicks).
 * Preserves in-page anchor navigation (when a hash is present).
 *
 * Also forces page-load (browser refresh / direct navigation) to start at the
 * very top by disabling the browser's automatic scroll restoration. Without
 * this, Chrome/Safari restore the previous scroll position on refresh, which
 * lands users mid-page on long routes.
 */
const ScrollToTop = () => {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();

  // Disable the browser's automatic scroll restoration as early as possible
  // (synchronously, before paint) so a hard refresh always lands at y=0.
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    if ("scrollRestoration" in window.history) {
      try {
        window.history.scrollRestoration = "manual";
      } catch {
        // Some embedded contexts (preview iframes) restrict history mutation.
      }
    }
    // On the very first mount (page load / refresh), force the top unless
    // the URL contains an in-page anchor we should honor.
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hash) return; // let anchor links handle their own scroll
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash, key, navigationType]);

  return null;
};

export default ScrollToTop;
